import { RefreshCwIcon, TriangleAlertIcon } from "lucide-react"
import { useEffect } from "react"
import { useRouteError } from "react-router"
import { PageBody, PageHeader, RefusalState } from "@/design-system/index.ts"
import { isChunkLoadError } from "@/installed-app/chunk-error.ts"
import { chunkMissing, reloadToUpdate, sayElsewhere } from "@/installed-app/updates.ts"

/*
 * What a route shows when rendering or loading it throws. Most often it is a page's code that a
 * new release has replaced, which says Beacon has been updated, with Reload taking the release
 * (`installed-app/updates.ts`); offline, the same failure is the connection's, and says something
 * went wrong. The error itself stays in the browser — it may hold what a Member typed — so nothing
 * here reports it anywhere.
 */

const reload = () => window.location.reload()

/**
 * Whether the route's error is its code failing to load with the network there, said as an update;
 * while the page says so, the update notice leaves it to the page.
 */
function useFailedAsUpdate() {
  const error = useRouteError()
  const updated = isChunkLoadError(error) && navigator.onLine
  useEffect(() => {
    if (!updated) return
    chunkMissing()
    return sayElsewhere()
  }, [updated])
  return updated
}

/** A route's failure inside the shell's panel, under a header of its own. */
export function RouteError() {
  const updated = useFailedAsUpdate()
  return (
    <>
      <PageHeader
        crumbs={[
          {
            label: updated ? "Beacon has been updated" : "Something went wrong",
            glyph: { kind: "icon", icon: updated ? RefreshCwIcon : TriangleAlertIcon },
          },
        ]}
      />
      <PageBody width="document">
        {updated ? (
          <RefusalState refusal="updated" onRetry={reloadToUpdate} />
        ) : (
          <RefusalState refusal="unknown" onRetry={reload} />
        )}
      </PageBody>
    </>
  )
}

/**
 * The shell's own failure, on the bare canvas, since there is no shell to put it in: a route
 * outside it that failed (its code a release replaced, or anything else), or the shell itself,
 * which says `refusal` (`offline` when it opened with no network). On the compact layout it is the
 * panel's own surface to the screen's edges, as the frame would be, with the message centred in it
 * rather than on a card.
 */
export function ShellError({
  refusal,
  onRetry = reload,
}: {
  refusal?: "unknown" | "offline"
  onRetry?: () => void
}) {
  const updated = useFailedAsUpdate()
  return (
    <main className="grid min-h-svh place-items-center bg-sidebar px-4 compact:min-h-dvh compact:bg-background compact:px-safe compact:pt-safe compact:pb-safe">
      <div className="w-md rounded-xl bg-background px-8 shadow-dialog compact:w-full compact:max-w-md compact:shadow-none compact:px-6">
        {refusal === undefined && updated ? (
          <RefusalState refusal="updated" onRetry={reloadToUpdate} />
        ) : (
          <RefusalState refusal={refusal ?? "unknown"} onRetry={onRetry} />
        )}
      </div>
    </main>
  )
}
